import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  Building2,
  ChevronRight,
  Clock,
  Key,
  Package,
  Play,
  Plus,
  ShieldCheck,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PageHeader, StatusPill } from '@/components/page';
import { EmptyState, MarkTile, Notice, ago, fmtDate, plural, rowLink } from '@/components/workspace-ui';
import { auditApi, keysApi, productsApi, usersApi } from '@/api';
import { useAuth, useClients, useOrganizations, useProducts } from '@/hooks';
import { cn } from '@/lib/utils';

const DAY = 864e5;
const ACCESS_LABEL: Record<string, string> = {
  gated: 'Gated',
  org_members: 'Org members',
  open: 'Open',
};

function auditTone(action: string): { tone: 'bad' | 'warn'; label: string } | null {
  if (/failed|denied/.test(action)) return { tone: 'bad', label: /denied/.test(action) ? 'Denied' : 'Failed' };
  if (/revoked|suspended|disabled/.test(action)) {
    return {
      tone: 'warn',
      label: action.includes('suspended') ? 'Suspended' : action.includes('disabled') ? 'Disabled' : 'Revoked',
    };
  }
  return null;
}

type GrantState = 'active' | 'expiring' | 'expired' | 'suspended';
const GRANT_TONE = { active: 'ok', expiring: 'warn', expired: 'bad', suspended: 'warn' } as const;
const GRANT_LABEL: Record<GrantState, string> = {
  active: 'Active',
  expiring: 'Expiring',
  expired: 'Expired',
  suspended: 'Suspended',
};

function grantState(g: { expiresAt: string | null; isGrantActive?: boolean }): GrantState {
  if (g.isGrantActive === false) return 'suspended';
  if (g.expiresAt) {
    const left = new Date(g.expiresAt).getTime() - Date.now();
    if (left <= 0) return 'expired';
    if (left < 14 * DAY) return 'expiring';
  }
  return 'active';
}

function CardShell({
  title,
  sub,
  action,
  children,
}: {
  title: string;
  sub?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-lg border border-border bg-card" aria-label={title}>
      <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3.5">
        <div className="min-w-0">
          <h2 className="text-[14.5px] font-semibold">{title}</h2>
          {sub && <p className="mt-0.5 text-[12.5px] text-muted-foreground">{sub}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function SkeletonRows({ rows = 3 }: { rows?: number }) {
  return (
    <div className="divide-y divide-border" aria-busy="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-3">
          <Skeleton className="h-8 w-8 rounded-lg" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-32" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const admin = user?.is_super_admin === true;

  const { data: orgs = [], isLoading: orgsLoading, isError: orgsError } = useOrganizations();
  const { data: products = [], isLoading: productsLoading } = useProducts();
  const { data: clients = [], isLoading: clientsLoading } = useClients();

  const { data: usersPage, isLoading: usersLoading } = useQuery({
    queryKey: ['users', 'page', { pageSize: 100 }],
    queryFn: () => usersApi.listPage({ pageSize: 100 }),
    enabled: admin,
  });
  const { data: keys = [] } = useQuery({
    queryKey: ['keys'],
    queryFn: keysApi.list,
    enabled: admin,
  });
  const { data: audit = [], isLoading: auditLoading } = useQuery({
    queryKey: ['audit', 'recent'],
    queryFn: () => auditApi.list({ limit: 8 }),
    enabled: admin,
  });
  const { data: failed24 } = useQuery({
    queryKey: ['audit', 'failed-24h'],
    queryFn: async () =>
      (
        await auditApi.list({
          action: 'auth.login.failed',
          startDate: new Date(Date.now() - DAY).toISOString(),
          limit: 100,
        })
      ).length,
    enabled: admin,
  });
  const { data: grants = [], isLoading: grantsLoading } = useQuery({
    queryKey: ['my-access'],
    queryFn: productsApi.myAccess,
    enabled: !admin && !!user,
  });

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const first = (user?.name ?? '').split(' ')[0] || 'there';

  const inactiveOrgs = orgs.filter((o) => !o.is_active).length;
  const accessCount = (m: string) => products.filter((p) => (p.settings?.access_model ?? 'gated') === m).length;
  const confidential = clients.filter((c) => c.client_type === 'confidential').length;
  const users = usersPage?.data ?? [];
  const usersTotal = usersPage?.pagination.total ?? users.length;
  const unverified = users.filter((u) => !u.emailVerified).length;
  const attention = grants.filter((g) => grantState(g) !== 'active').length;

  const stats: { href: string; icon: LucideIcon; label: string; value: number; note: string; loading: boolean }[] = [
    {
      href: '/organizations',
      icon: Building2,
      label: 'Organizations',
      value: orgs.length,
      note: inactiveOrgs ? `${inactiveOrgs} inactive` : 'All active',
      loading: orgsLoading,
    },
    {
      href: '/products',
      icon: Package,
      label: 'Apps',
      value: products.length,
      note: `${accessCount('gated')} gated · ${accessCount('org_members')} org-wide · ${accessCount('open')} open`,
      loading: productsLoading,
    },
    {
      href: '/clients',
      icon: Key,
      label: 'OAuth clients',
      value: clients.length,
      note: `${confidential} confidential · ${clients.length - confidential} public`,
      loading: clientsLoading,
    },
    admin
      ? {
          href: '/users',
          icon: Users,
          label: 'Users',
          value: usersTotal,
          note:
            usersTotal > users.length
              ? 'Across the workspace'
              : unverified
                ? `${unverified} with unverified email`
                : 'All emails verified',
          loading: usersLoading,
        }
      : {
          href: '/access',
          icon: ShieldCheck,
          label: 'My app grants',
          value: grants.length,
          note: attention ? `${attention} need attention` : 'All active',
          loading: grantsLoading,
        },
  ];

  const retiring = admin
    ? keys.filter(
        (k) =>
          k.is_active &&
          !k.is_current &&
          k.expires_at &&
          new Date(k.expires_at).getTime() > Date.now() &&
          new Date(k.expires_at).getTime() - Date.now() < 14 * DAY
      )
    : [];

  const sortedApps = [...products].sort(
    (a, b) =>
      Number(b.is_active) - Number(a.is_active) ||
      new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
  );
  const clientsByProduct = new Map<string, number>();
  clients.forEach((c) => clientsByProduct.set(c.product_id, (clientsByProduct.get(c.product_id) ?? 0) + 1));

  const myOrg = orgs.find((o) => o.role);
  const orgList = orgs.slice(0, 6);

  const quickActions: { to: string; icon: LucideIcon; title: string; body: string }[] = [
    { to: '/organizations/new', icon: Building2, title: 'New organization', body: 'Group people and apps under one owner.' },
    { to: '/products/new', icon: Package, title: 'Register an app', body: 'Define roles, permissions and access rules.' },
    { to: '/clients/new', icon: Key, title: 'Add an OAuth client', body: 'Get a client_id and redirect URIs.' },
    { to: '/playground', icon: Play, title: 'OAuth playground', body: 'Run the authorization code flow end to end.' },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Overview"
        title={`${greeting}, ${first}`}
        description={
          admin
            ? `Super admin · ${plural(orgs.length, 'organization')}, ${plural(products.length, 'app')} and ${plural(clients.length, 'OAuth client')} across the workspace.`
            : `${myOrg ? `${myOrg.name} · ${myOrg.role}` : 'No organization yet'}. Manage the apps and OAuth clients your organizations own.`
        }
        actions={
          <>
            <Button asChild variant="secondary">
              <Link to="/organizations/new">
                <Building2 />
                New organization
              </Link>
            </Button>
            {admin && (
              <Button asChild>
                <Link to="/products/new">
                  <Plus />
                  Register an app
                </Link>
              </Button>
            )}
          </>
        }
      />

      <div className="space-y-5">
        {retiring.map((k) => {
          const days = Math.max(1, Math.ceil((new Date(k.expires_at!).getTime() - Date.now()) / DAY));
          return (
            <Notice
              key={k.kid}
              tone="warn"
              title={
                <>
                  Signing key <code className="font-mono">{k.kid}</code> retires in {plural(days, 'day')}
                </>
              }
              action={
                <Button asChild variant="secondary" size="sm">
                  <Link to="/keys">Review keys</Link>
                </Button>
              }
            >
              It stays in the JWKS until {fmtDate(k.expires_at)}, so tokens it signed keep verifying until then.
              The current key is already signing new tokens.
            </Notice>
          );
        })}

        {orgsError && (
          <Notice tone="bad" title="Couldn’t load organizations">
            Refresh the page to try again.
          </Notice>
        )}

        <nav
          aria-label="Workspace totals"
          className="grid grid-cols-1 divide-y divide-border overflow-hidden rounded-lg border border-border bg-card sm:grid-cols-2 sm:divide-x sm:divide-y-0 lg:grid-cols-4"
        >
          {stats.map((s) => (
            <Link
              key={s.label}
              to={s.href}
              className="group block px-[18px] py-3.5 transition-colors hover:bg-foreground/[0.03] sm:[&:nth-child(n+3)]:border-t sm:[&:nth-child(n+3)]:border-border lg:[&:nth-child(n+3)]:border-t-0"
            >
              <p className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.06em] text-muted-foreground">
                <s.icon className="h-3.5 w-3.5" />
                {s.label}
              </p>
              {s.loading ? (
                <Skeleton className="mt-2 h-7 w-12" />
              ) : (
                <p className="mt-1.5 font-mono text-[26px] font-semibold tabular-nums tracking-[-0.03em]">{s.value}</p>
              )}
              <p className="mt-0.5 truncate text-xs text-muted-foreground">{s.loading ? ' ' : s.note}</p>
            </Link>
          ))}
        </nav>

        <div className="grid gap-5 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
          <div className="min-w-0 space-y-5">
            <CardShell
              title={admin ? 'Apps' : 'Your apps'}
              sub={admin ? 'Every app registered in this workspace.' : 'Apps owned by your organizations.'}
              action={
                <Button asChild variant="ghost" size="sm">
                  <Link to="/products">
                    View all
                    <ArrowRight />
                  </Link>
                </Button>
              }
            >
              {productsLoading ? (
                <SkeletonRows />
              ) : sortedApps.length ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>App</TableHead>
                      <TableHead className="hidden sm:table-cell">Organization</TableHead>
                      <TableHead>Access</TableHead>
                      <TableHead className="text-right">Clients</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedApps.slice(0, 8).map((p) => {
                      const href = `/products/${p.id}`;
                      return (
                        <TableRow
                          key={p.id}
                          className={cn(rowLink, !p.is_active && 'opacity-60')}
                          onClick={(e) => {
                            if (!(e.target as HTMLElement).closest('a,button')) navigate(href);
                          }}
                        >
                          <TableCell>
                            <div className="flex items-center gap-2.5">
                              <MarkTile name={p.name} />
                              <div className="flex min-w-0 flex-col">
                                <Link to={href} className="truncate font-semibold hover:underline">
                                  {p.name}
                                </Link>
                                <span className="truncate font-mono text-xs text-muted-foreground">{p.slug}</span>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="hidden max-w-[180px] truncate sm:table-cell">
                            {p.organization_name ?? '—'}
                          </TableCell>
                          <TableCell>
                            <span className="rounded-[5px] border border-border px-1.5 py-0.5 text-[11.5px] text-muted-foreground">
                              {ACCESS_LABEL[p.settings?.access_model ?? 'gated']}
                            </span>
                          </TableCell>
                          <TableCell className="text-right font-mono tabular-nums">
                            {clientsByProduct.get(p.id) ?? 0}
                          </TableCell>
                          <TableCell>
                            <StatusPill tone={p.is_active ? 'ok' : 'neutral'}>
                              {p.is_active ? 'Active' : 'Inactive'}
                            </StatusPill>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <EmptyState
                  icon={Package}
                  title="No apps yet"
                  body="Register an app to define its roles and who can sign in to it."
                  action={
                    admin ? (
                      <Button asChild variant="secondary">
                        <Link to="/products/new">
                          <Plus />
                          Register an app
                        </Link>
                      </Button>
                    ) : undefined
                  }
                />
              )}
            </CardShell>

            {admin ? (
              <CardShell
                title="Recent activity"
                sub={
                  failed24
                    ? `${plural(failed24, 'failed sign-in')} in the last 24 hours.`
                    : 'No failed sign-ins in the last 24 hours.'
                }
                action={
                  <Button asChild variant="ghost" size="sm">
                    <Link to="/audit">
                      Audit log
                      <ArrowRight />
                    </Link>
                  </Button>
                }
              >
                {auditLoading ? (
                  <SkeletonRows />
                ) : audit.length ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Event</TableHead>
                        <TableHead className="hidden sm:table-cell">Actor</TableHead>
                        <TableHead className="text-right">When</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {audit.slice(0, 8).map((e) => {
                        const t = auditTone(e.action);
                        return (
                          <TableRow key={e.id} className={rowLink} onClick={() => navigate('/audit')}>
                            <TableCell>
                              <div className="flex min-w-0 flex-col">
                                <span className="flex items-center gap-2">
                                  <Link to="/audit" className="font-mono text-[12.5px] hover:underline">
                                    {e.action}
                                  </Link>
                                  {t && <StatusPill tone={t.tone}>{t.label}</StatusPill>}
                                </span>
                                <span
                                  className="max-w-[260px] truncate font-mono text-xs text-muted-foreground"
                                  title={`${e.resource} ${e.resourceId ?? ''}`}
                                >
                                  {e.resource}
                                  {e.resourceId ? ` · ${e.resourceId}` : ''}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="hidden sm:table-cell">
                              <div className="flex flex-col">
                                {e.userId ? (
                                  <span className="max-w-[160px] truncate font-mono text-xs">{e.userId}</span>
                                ) : (
                                  <span className="text-muted-foreground">Unauthenticated</span>
                                )}
                                <span className="font-mono text-xs text-muted-foreground">{e.ipAddress ?? '—'}</span>
                              </div>
                            </TableCell>
                            <TableCell
                              className="whitespace-nowrap text-right font-mono text-xs text-muted-foreground"
                              title={new Date(e.timestamp).toLocaleString()}
                            >
                              {ago(e.timestamp)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <EmptyState icon={Clock} title="No activity yet" body="Sign-ins and admin changes appear here as they happen." />
                )}
              </CardShell>
            ) : (
              <CardShell
                title="Your access"
                sub="Roles granted to you, per app."
                action={
                  <Button asChild variant="ghost" size="sm">
                    <Link to="/access">
                      My access
                      <ArrowRight />
                    </Link>
                  </Button>
                }
              >
                {grantsLoading ? (
                  <SkeletonRows />
                ) : grants.length ? (
                  <ul className="divide-y divide-border">
                    {grants.map((g) => {
                      const st = grantState(g);
                      return (
                        <li key={g.productId}>
                          <Link
                            to="/access"
                            className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-foreground/[0.03]"
                          >
                            <MarkTile name={g.name} />
                            <div className="min-w-0 flex-1">
                              <p className="truncate font-semibold">{g.name}</p>
                              <p className="mt-0.5 flex flex-wrap gap-1">
                                {g.roles.length ? (
                                  g.roles.map((r) => (
                                    <span key={r} className="rounded-[5px] bg-foreground/[0.06] px-1.5 py-0.5 font-mono text-[11.5px]">
                                      {r}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-xs text-muted-foreground">
                                    {g.permissions.length
                                      ? plural(g.permissions.length, 'direct permission')
                                      : 'No roles'}
                                  </span>
                                )}
                              </p>
                            </div>
                            <StatusPill tone={GRANT_TONE[st]}>{GRANT_LABEL[st]}</StatusPill>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <EmptyState
                    icon={ShieldCheck}
                    title="No app access yet"
                    body="An organization admin grants app roles. Once they do, the apps appear here."
                  />
                )}
              </CardShell>
            )}
          </div>

          <div className="min-w-0 space-y-5">
            <CardShell
              title={admin ? 'Organizations' : 'Your organizations'}
              action={
                <Button asChild variant="ghost" size="sm">
                  <Link to="/organizations">View all</Link>
                </Button>
              }
            >
              {orgsLoading ? (
                <SkeletonRows />
              ) : orgList.length ? (
                <ul className="divide-y divide-border">
                  {orgList.map((o) => (
                    <li key={o.id}>
                      <Link
                        to={`/organizations/${o.id}`}
                        className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-foreground/[0.03]"
                      >
                        <MarkTile name={o.name} />
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-semibold">{o.name}</p>
                          <p className="truncate font-mono text-xs text-muted-foreground">
                            {o.slug}
                            {o.member_count !== undefined ? ` · ${plural(o.member_count, 'member')}` : ''}
                          </p>
                        </div>
                        {!o.is_active ? (
                          <StatusPill>Inactive</StatusPill>
                        ) : o.role ? (
                          <span className="rounded-full bg-foreground/[0.06] px-2 py-0.5 text-[11.5px] font-semibold text-muted-foreground">
                            {o.role}
                          </span>
                        ) : null}
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState
                  icon={Building2}
                  title="No organizations yet"
                  body="Create one to own apps and invite your team."
                  action={
                    <Button asChild variant="secondary">
                      <Link to="/organizations/new">
                        <Plus />
                        New organization
                      </Link>
                    </Button>
                  }
                />
              )}
            </CardShell>

            <CardShell title="Get things done">
              <ul className="divide-y divide-border">
                {quickActions
                  .filter((q) => admin || q.to === '/organizations/new' || q.to === '/playground')
                  .map((q) => (
                  <li key={q.to}>
                    <Link
                      to={q.to}
                      className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-foreground/[0.03]"
                    >
                      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border text-muted-foreground">
                        <q.icon className="h-4 w-4" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold">{q.title}</p>
                        <p className="text-xs text-muted-foreground">{q.body}</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </Link>
                  </li>
                ))}
              </ul>
            </CardShell>
          </div>
        </div>
      </div>
    </div>
  );
}
